import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import ContactSelector from '../ContactSelector/ContactSelector';
import CargoTypeSelector from '../CargoTypeSelector/CargoTypeSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import { cargoTypeSelectorSelector } from '../CargoTypeSelector/reducers/CargoTypeSelectorReducer';
import PortSelector from '../PortSelector/PortSelector';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import PortModel from '../PortSelector/models/PortModel';
import { GetMarketInfoByIdParam } from './api/MarketInfoApi';
import BaseResponse from '../../app/BaseResponse';
import MarketInfoModel from './models/MarketInfoModel';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import ContactNameModel from '../ContactSelector/models/ContactNameModel';
import { contactSelectorSelector, setContactSearchResults } from '../ContactSelector/reducers/ContactSelectorReducer';
import { MarketInfoInputSelector, resetMarketInfoInput, setCargoTypeId, setCargoTypeName, setChartererId, setChartererName, setChartererRate, setDisport, setDuration, setInputExpand, setLoadPort, setMarketInfoInput, setOwnerId, setOwnerName, setOwnerRate, setRemarks, setSelectedMarketInfoId, setSize, setTonnageId, setTonnageVesselName, setTriggerFocusVesselNameInput, setTriggerSave, setTriggerSelectMarketInfo, setType } from './reducers/MarketInfoInputReducer';
import MarketInfoApi from './api/MarketInfoApi';
import TonnageNameSelector from '../Tonnage/components/TonnageNameSelector';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';
import { tonnageInputSelector } from '../Tonnage/reducers/TonnageInputReducer';
import { MarketInfoType } from './models/MarketInfoQueryModel';

const MarketInfoInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const marketInfoInputViewData = useSelector(MarketInfoInputSelector);
    let portSelectorData = useSelector(portSelectorSelector);
    const contactSelectorData = useSelector(contactSelectorSelector);
    const tonnageViewData = useSelector(tonnageInputSelector);
    let cargoTypeSelectorData = useSelector(cargoTypeSelectorSelector);

    useEffect(() => {
        if (marketInfoInputViewData.triggerSave > 0) {
            save();
            dispatch(setTriggerSave(0));
        }
    }, [marketInfoInputViewData.triggerSave]);

    useEffect(() => {
        if (marketInfoInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [marketInfoInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetMarketInfoInput());
        dispatch(setSelectedMarketInfoId(0));
        dispatch(setTriggerFocusVesselNameInput((new Date()).getTime()));
        dispatch(setTriggerSelectMarketInfo(new Date().getTime()));
    }

    useEffect(() => {
        if (marketInfoInputViewData.selectedMarketInfoId > 0) {
            selectMarketInfoById(marketInfoInputViewData.selectedMarketInfoId);
        }
    }, [marketInfoInputViewData.selectedMarketInfoId])

    const selectMarketInfoById = (id: number) => {
        dispatch(showLoading(true));
        MarketInfoApi.GetById({
            id: id
        } as GetMarketInfoByIdParam).then((response: BaseResponse<MarketInfoModel>) => {
            if (response.success) {

                setContactForContactSelector(response.data);

                dispatch(setMarketInfoInput(response.data));

                dispatch(setTriggerSelectMarketInfo(new Date().getTime()));

                dispatch(showLoading(false));
            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const setContactForContactSelector = (data: MarketInfoModel) => {
        let searchResults = Utility.CloneObject(contactSelectorData.searchResults) as ContactNameModel[];

        if (data.chartererId) {
            searchResults.push({
                id: data.chartererId,
                companyName: data.chartererName
            } as ContactNameModel);
        }

        if (data.ownerId) {
            searchResults.push({
                id: data.ownerId,
                companyName: data.ownerName
            } as ContactNameModel);
        }

        searchResults = _.uniqBy(searchResults, (item: ContactNameModel) => {
            return item.id;
        });
        dispatch(setContactSearchResults(searchResults));
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    const save = () => {
        try {
            validate();

            MarketInfoApi.Save(marketInfoInputViewData.input)
                .then((response: BaseResponse<MarketInfoModel>) => {
                    if (response.success) {
                        if (marketInfoInputViewData.input.id === 0) {
                            Utility.Alert.Success("Market Info added successful");
                        } else {
                            Utility.Alert.Success("Market Info updated successful");
                        }

                        refreshInput();
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const getCargoTypeById = (cargoTypeId: number) => {
        let selectedCargoType = _.find(cargoTypeSelectorData.cargoTypes, (cargo) => {
            return cargo.id === cargoTypeId;
        });

        if (selectedCargoType) {
            return selectedCargoType;
        }

        return null;
    }

    const validate = () => {
        return true;
    }

    return (
        <>
            <CustomAccordion
                title="Input"
                defaultOpen={true}
                className="top-quick-input"
                open={marketInfoInputViewData.inputExpand}
                onOpenChange={(isOpen: boolean) => {
                    dispatch(setInputExpand(isOpen));
                }}
            >
                <Grid container>
                    <Grid item lg={3} xs={12}>
                        <Grid container className='cargo-input-component'>
                            <Grid xs={10} lg={9}>
                                <TonnageNameSelector
                                    triggerFocusInput={marketInfoInputViewData.triggerFocusVesselNameInput}
                                    selectedTonnageId={marketInfoInputViewData.input.tonnageId ? marketInfoInputViewData.input.tonnageId : ""}
                                    currentVesselName={marketInfoInputViewData.input.tonnageVesselName ? marketInfoInputViewData.input.tonnageVesselName : ""}
                                    onTonnageIdChange={(value: string) => {
                                        dispatch(setTonnageId(value));
                                        let selectedTonnageName = _.find(tonnageViewData.tonnageNames, (tonnageName) => {
                                            return tonnageName.id == value;
                                        });
                                        setTimeout(() => {
                                            if (selectedTonnageName) {
                                                dispatch(setSize(selectedTonnageName.dwt));
                                                dispatch(setTonnageVesselName(selectedTonnageName.vesselName));
                                            }
                                        }, 50);
                                    }}
                                    onVesselNameChange={(value: string) => {
                                        dispatch(setTonnageVesselName(value));
                                    }}
                                >
                                </TonnageNameSelector>
                            </Grid>
                            <Grid item xs={2} lg={3}>
                                <Tooltip title="Reset (Alt + R)">
                                    <Button
                                        className="clear-input-button"
                                        color="success"
                                        variant="contained"
                                        tabIndex={-1}
                                        onClick={() => {
                                            refreshInput();
                                        }}
                                    >
                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        <TextField
                            className="cargo-input-component"
                            fullWidth
                            label="Size"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={marketInfoInputViewData.input.size ? marketInfoInputViewData.input.size : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setSize(value));
                            }}
                        >
                        </TextField>
                        <ContactSelector
                            label='Owners'
                            className="cargo-input-component"
                            selectedContactId={marketInfoInputViewData.input.ownerId ? marketInfoInputViewData.input.ownerId : ""}
                            currentContactName={marketInfoInputViewData.input.ownerName ? marketInfoInputViewData.input.ownerName : ""}
                            useFreeText={true}
                            triggerInitContactName={marketInfoInputViewData.triggerSelectMarketInfo}
                            onContactIdChange={(value: string) => {
                                dispatch(setOwnerId(value));
                            }}
                            onContactNameChange={(value: string) => {
                                dispatch(setOwnerName(value));
                            }}
                        >
                        </ContactSelector>
                        <ContactSelector
                            label='Charterers'
                            className="cargo-input-component"
                            selectedContactId={marketInfoInputViewData.input.chartererId ? marketInfoInputViewData.input.chartererId : ""}
                            currentContactName={marketInfoInputViewData.input.chartererName ? marketInfoInputViewData.input.chartererName : ""}
                            useFreeText={true}
                            triggerInitContactName={marketInfoInputViewData.triggerSelectMarketInfo}
                            onContactIdChange={(value: string) => {
                                dispatch(setChartererId(value));
                            }}
                            onContactNameChange={(value: string) => {
                                dispatch(setChartererName(value));
                            }}
                        >
                        </ContactSelector>
                    </Grid>
                    <Grid item lg={4} xs={12}>
                        <Autocomplete
                            className='cargo-input-component'
                            disableClearable={true}
                            options={[MarketInfoType.Voy, MarketInfoType.TCT, MarketInfoType.Period]}
                            renderInput={(params) => <TextField {...params} label="Biz" />}
                            value={marketInfoInputViewData.input.type}
                            onChange={(event: React.SyntheticEvent, value: MarketInfoType, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                dispatch(setType(value));
                            }}
                        />
                        <CargoTypeSelector
                            className='cargo-input-component'
                            isGroupDisabled={true}
                            onChange={(cargoType: CargoTypeModel | null) => {
                                if (cargoType) {
                                    dispatch(setCargoTypeId((cargoType as CargoTypeModel).id));
                                    dispatch(setCargoTypeName((cargoType as CargoTypeModel).name));
                                }
                            }}
                            cargoTypeValue={marketInfoInputViewData.input.cargoTypeId ? getCargoTypeById(marketInfoInputViewData.input.cargoTypeId) : null}
                        >
                        </CargoTypeSelector>
                        <PortSelector
                            onPortChange={(value: PortModel | null) => {
                                if (value) {
                                    dispatch(setLoadPort((value as PortModel).id));
                                } else {
                                    dispatch(setLoadPort(null));
                                }
                            }}
                            className="cargo-input-component"
                            portLabel='Load/Delivery'
                            areaLabel='Area'
                            isAreaDisabled={true}
                            portValue={marketInfoInputViewData.input.loadPort ? getPortById(marketInfoInputViewData.input.loadPort) : null}
                            isAreaRelatedToPort={true}
                        >
                        </PortSelector>
                        <PortSelector
                            onPortChange={(value: PortModel | null) => {
                                if (value) {
                                    dispatch(setDisport((value as PortModel).id));
                                } else {
                                    dispatch(setDisport(null));
                                }
                            }}
                            className="cargo-input-component"
                            portLabel='Dis/Redel'
                            areaLabel='Area'
                            isAreaDisabled={true}
                            portValue={marketInfoInputViewData.input.disport ? getPortById(marketInfoInputViewData.input.disport) : null}
                            isAreaRelatedToPort={true}
                        >
                        </PortSelector>
                    </Grid>
                    <Grid item lg={4} xs={12}>
                        <TextField
                            fullWidth
                            className="cargo-input-component"
                            label={marketInfoInputViewData.input.type === MarketInfoType.Voy ? "Quantity" : "Duration"}
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={marketInfoInputViewData.input.duration ? marketInfoInputViewData.input.duration : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setDuration(value));
                            }}
                        >
                        </TextField>
                        <TextField
                            fullWidth
                            className="cargo-input-component"
                            label="Owners $"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={marketInfoInputViewData.input.ownerRate ? marketInfoInputViewData.input.ownerRate : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setOwnerRate(value));
                            }}
                        >
                        </TextField>
                        <TextField
                            fullWidth
                            className="cargo-input-component"
                            label="Charterers $"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={marketInfoInputViewData.input.chartererRate ? marketInfoInputViewData.input.chartererRate : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setChartererRate(value));
                            }}
                        >
                        </TextField>
                        <TextField
                            fullWidth
                            className="cargo-input-component"
                            label="Remarks"
                            value={marketInfoInputViewData.input.remarks ? marketInfoInputViewData.input.remarks : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? "" : event.target.value;
                                dispatch(setRemarks(value));
                            }}
                        >
                        </TextField>
                    </Grid>
                    <Grid item lg={1} xs={12}
                    >
                        <Tooltip title="Alt + S">
                            <Button
                                onClick={save}
                                variant="outlined"
                            >
                                Save (s)
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
            </CustomAccordion>
        </>
    )
};

export default MarketInfoInput;